<template>
  <div>
    <meta name="apple-itunes-app" :content="appleItunesAppId" />
    <!-- `app-banner` is the gate, not decoration: it is display:none until
         the pre-paint script in index.html decides this visitor gets a banner.
         `d-flex` is deliberately absent — the gate supplies the display, and a
         Vuetify utility would out-!important it. -->
    <div
      v-if="siteHasAppBanner"
      class="app-banner align-center pt-1 pb-2 bg-white position-relative l-spacing-half"
    >
      <div class="d-flex align-center pl-3 pr-2">
        <img
          width="40"
          height="40"
          id="app-icon"
          src="@/assets/images/icons/app_icon.png"
          alt="vicampo-app-icon"
        />
      </div>
      <a
        class="pr-5 d-flex flex-column"
        :href="appLink"
        rel="noreferrer"
        target="_blank"
      >
        <div class="pb-1">
          <span class="c-gray font-size-m bold">VICAMPO APP</span>
          <Stars class="mx-1 font-size-m" />
        </div>
        <VBtnSecondary class="font-size-xs">{{
          translate('fe_app_pdp_download_now')
        }}</VBtnSecondary>
      </a>
      <button
        type="button"
        class="close"
        @click="close"
        aria-label="close-banner"
      >
        <SvgWrapper use="icon_svg_close" dimensions="20" />
      </button>
    </div>
  </div>
</template>

<script>
import SiteConfig from '@/services/SiteConfig'
import { defineAsyncComponent } from 'vue'

const Stars = defineAsyncComponent(
  () => import('@/components/basic/atoms/Stars.vue')
)
const SvgWrapper = defineAsyncComponent(
  () => import('@/components/basic/atoms/SvgWrapper.vue')
)
const APP_LINK =
  'https://play.google.com/store/apps/details?id=de.vicampo.android.customersapp'
// Kept in step with the pre-paint script in index.html, which reads the same
// key to decide whether this visitor still gets a banner.
const BANNER_HIDDEN = 'banner_hidden'
const HAS_BANNER_CLASS = 'has-app-banner'

export default {
  components: { Stars, SvgWrapper },
  computed: {
    /**
     * Whether this shop has an app banner at all — the only half of the
     * decision a render may make.
     *
     * It comes from the site id, so the server and the hydrating client
     * always agree on it. The other half — is this an Android device, and has
     * this visitor dismissed the banner — is visitor-specific, which an
     * anonymous response shared through the CDN cache cannot carry. That half
     * is settled by the pre-paint script in index.html, before the first
     * frame, so the banner never displaces content that is already on screen
     * (VIP-12333 moved it out of created() to stop a hydration mismatch; the
     * markup was still being inserted after paint, which is the layout shift
     * this replaces).
     */
    siteHasAppBanner() {
      return SiteConfig.appBanner
    },
    appLink() {
      return APP_LINK
    },
    appleItunesAppId() {
      return SiteConfig.appleItunesAppId
    }
  },
  methods: {
    /**
     * Dismissal drops the class the pre-paint script set rather than a local
     * flag, so it also holds for the AppBanner the next route mounts — and,
     * through localStorage, for the next visit.
     */
    close() {
      localStorage.setItem(BANNER_HIDDEN, 'true')
      document.documentElement.classList.remove(HAS_BANNER_CLASS)
    }
  }
}
</script>

<style lang="css" scoped>
.close {
  position: absolute;
  padding: 10px 10px 25px 0;
  right: 0;
}

.close > svg {
  stroke-width: 1px;
}

#app-icon {
  max-width: 40px;
  max-height: 40px;
  -webkit-box-shadow: rgba(0, 0, 0, 0.25) 0 1px 3px 0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.25);
  border-radius: 0.5rem;
}

a > :first-child {
  word-break: break-all;
}

span.btn {
  min-height: 1.1rem;
  display: inline-flex;
  min-width: 135px;
  max-width: 132px;
  height: 1.1rem;
  padding: 0.6rem 0.3rem;
}
</style>
